import * as React from 'react';
import { graphql } from 'react-apollo';
import Media from 'react-media';

import gql from 'graphql-tag';
import { compose } from 'recompose';

import connectToStores from 'js/lib/connectToStores';
import type UserAgentInfo from 'js/lib/useragent';

import { Col, Container, Row } from '@coursera/coursera-ui';

import CourseRecords from 'bundles/accomplishments/components/s12n-certificates/CourseRecords';
import S12nAccomplishmentsDataProvider from 'bundles/accomplishments/components/s12n-certificates/S12nAccomplishmentsDataProvider';
import S12nCertificate from 'bundles/accomplishments/components/s12n-certificates/S12nCertificate';
import S12nFaqs from 'bundles/accomplishments/components/s12n-certificates/S12nFaqs';
import type { XdpSpecializationMetadataQuery_XdpV1Resource_slug_elements_xdpMetadata_XdpV1_sdpMetadataMember_sdpMetadata as SDPMetadata } from 'bundles/accomplishments/components/s12n-certificates/__generated__/XdpSpecializationMetadataQuery';
import type { S12nCourseCertificate } from 'bundles/accomplishments/components/s12n-certificates/types';
import CertificateSharing from 'bundles/accomplishments/components/shared/CertificateSharing';
import CompletionOverview from 'bundles/accomplishments/components/shared/CompletionOverview';
import PageHeader from 'bundles/accomplishments/components/shared/PageHeader';
import ProductOverviewSnippet from 'bundles/accomplishments/components/shared/ProductOverviewSnippet';
import constants from 'bundles/accomplishments/constants';
import LearningObjectivesAndSkills from 'bundles/enterprise-legacy-xdp/components/LearningObjectivesAndSkills';
import { getS12nCertProductUtm } from 'bundles/s12n-common/lib/s12nProductVariantUtils';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';

import _t from 'i18n!nls/accomplishments';

type PropsFromCaller = {
  userName: string;
  s12nName: string;
  courseNames: Array<string>;
  certificateLink: string;
  certificateImageUrl?: string;
  userPhoto?: string;
  completionDate: string;
  partnerNames: string;
  s12nHomeLink: string;
  s12nFAQs: {
    answer: string;
    question: string;
  }[];
  certificateDescription: string;
  courseCerts: S12nCourseCertificate[];
  slug: string;
  isCertificateOwner?: boolean;
};

type PropsToComponent = PropsFromCaller & {
  sdpMetadata: SDPMetadata;
  userAgent: UserAgentInfo;
};

const S12nAccomplishmentPageContent = ({
  userName,
  s12nName,
  courseNames,
  certificateLink,
  certificateImageUrl,
  userPhoto,
  completionDate,
  partnerNames,
  s12nHomeLink,
  s12nFAQs,
  certificateDescription,
  courseCerts,
  slug,
  sdpMetadata,
  isCertificateOwner,
  userAgent,
}: PropsToComponent) => {
  const { id, ratings, partners, skills, cmlLearningObjectives, productVariant, totalContentWeeks } = sdpMetadata || {};
  const utmProductParam = getS12nCertProductUtm(productVariant);

  return (
    <Container>
      <PageHeader ownRecord={isCertificateOwner} name={s12nName} isSpecialization={true} />
      <S12nAccomplishmentsDataProvider slug={slug}>
        {({ enrollmentCount, learningMonths, learningHoursPerWeek }) => (
          <Media
            query={{ maxWidth: constants.MD_SCREEN_PX_BREAKPOINT - 1 }}
            defaultMatches={userAgent && userAgent.isMobileBrowser}
          >
            {(matches) => {
              // Mobile
              if (matches) {
                return (
                  <Row>
                    <Col md="6" rootClassName="left-col">
                      <S12nCertificate
                        userName={userName}
                        s12nName={s12nName}
                        certificateDescription={certificateDescription}
                        certificateLink={certificateLink}
                        certificateImageUrl={certificateImageUrl}
                        isCertificateOwner={isCertificateOwner}
                      />
                      {sdpMetadata && (
                        <ProductOverviewSnippet
                          id={id}
                          ratings={ratings}
                          partners={partners}
                          name={s12nName}
                          productLink={`/specializations/${slug}`}
                          isCertificateOwner={isCertificateOwner}
                          isS12n
                          enrollmentCount={enrollmentCount}
                        />
                      )}
                      <LearningObjectivesAndSkills skills={skills} cmlLearningObjectives={cmlLearningObjectives} />
                    </Col>
                    <Col md="6">
                      <CompletionOverview
                        userName={userName}
                        userPhoto={userPhoto}
                        completionDate={completionDate}
                        completedCourseNames={courseNames}
                        totalContentWeeks={totalContentWeeks}
                        partnerNames={partnerNames}
                        name={s12nName}
                        productLink={s12nHomeLink}
                        isS12n
                        totalLearningMonths={learningMonths}
                        totalLearningHoursPerWeek={learningHoursPerWeek}
                      />
                      <CertificateSharing
                        title={_t('Share this Certificate')}
                        productName={s12nName}
                        partnerName={partnerNames}
                        certificatePdfLink={certificateLink}
                        utmProductParam={utmProductParam}
                        isCertificateOwner={isCertificateOwner}
                      />
                    </Col>
                  </Row>
                );
              } else {
                // Desktop
                return (
                  <Row>
                    <Col md="6" rootClassName="left-col">
                      <CompletionOverview
                        userName={userName}
                        userPhoto={userPhoto}
                        completionDate={completionDate}
                        completedCourseNames={courseNames}
                        totalContentWeeks={totalContentWeeks}
                        partnerNames={partnerNames}
                        name={s12nName}
                        productLink={s12nHomeLink}
                        isS12n
                        totalLearningMonths={learningMonths}
                        totalLearningHoursPerWeek={learningHoursPerWeek}
                      />
                      {sdpMetadata && (
                        <ProductOverviewSnippet
                          id={id}
                          ratings={ratings}
                          partners={partners}
                          name={s12nName}
                          productLink={`/specializations/${slug}`}
                          isCertificateOwner={isCertificateOwner}
                          isS12n
                          enrollmentCount={enrollmentCount}
                        />
                      )}
                      <LearningObjectivesAndSkills skills={skills} cmlLearningObjectives={cmlLearningObjectives} />
                    </Col>
                    <Col md="6">
                      <S12nCertificate
                        userName={userName}
                        s12nName={s12nName}
                        certificateDescription={certificateDescription}
                        certificateLink={certificateLink}
                        certificateImageUrl={certificateImageUrl}
                        isCertificateOwner={isCertificateOwner}
                      />
                      <CertificateSharing
                        title={_t('Share this Certificate')}
                        productName={s12nName}
                        partnerName={partnerNames}
                        certificatePdfLink={certificateLink}
                        utmProductParam={utmProductParam}
                        isCertificateOwner={isCertificateOwner}
                      />
                    </Col>
                  </Row>
                );
              }
            }}
          </Media>
        )}
      </S12nAccomplishmentsDataProvider>
      <CourseRecords courseCerts={courseCerts} userName={userName} ownRecord={isCertificateOwner} isS12nCerts={true} />
      {isCertificateOwner && (
        <S12nFaqs
          s12nName={s12nName}
          partnerNames={partnerNames}
          completionDate={completionDate}
          specializationFAQs={s12nFAQs}
        />
      )}
    </Container>
  );
};

const XdpDataQuery = gql`
  query XdpSpecializationMetadataQuery($slug: String!) {
    XdpV1Resource {
      slug(productType: "SPECIALIZATION", slug: $slug) {
        elements {
          name
          id
          slug
          xdpMetadata {
            __typename
            ... on XdpV1_sdpMetadataMember {
              sdpMetadata {
                id
                slug
                totalContentWeeks
                cmlLearningObjectives {
                  ... on XdpV1_cmlMember {
                    cml {
                      value
                      dtdId
                    }
                  }
                }
                skills
                productVariant
                ratings {
                  averageFiveStarRating
                  ratingCount
                  commentCount
                }
                partners {
                  id
                  name
                  squareLogo
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default compose<PropsToComponent, PropsFromCaller>(
  connectToStores<PropsToComponent, PropsFromCaller>([ApplicationStoreClass], (ApplicationStore, props) => ({
    ...props,
    userAgent: ApplicationStore.getUserAgent(),
  })),
  graphql(XdpDataQuery, {
    props: ({ data: { XdpV1Resource } }: any) => {
      return {
        sdpMetadata: XdpV1Resource?.slug?.elements?.[0]?.xdpMetadata?.sdpMetadata,
      };
    },
  })
)(S12nAccomplishmentPageContent);
